import { tabsApiConnector } from 'apiConnector/tabs/tabs';
import { tabRemoved } from 'epic/workspace/common/tabs/tabsEpics';
import type { StaticFormResult } from 'models/forms/StaticFormResult';
import { ofType } from 'redux-observable';
import type { SchedulerLike } from 'rxjs';
import { of, throwError } from 'rxjs';
import { catchError, filter, mergeMap, takeUntil, timeout, withLatestFrom } from 'rxjs/operators';
import { commonFormActions } from 'state/forms/actions/form';
import type { EpicWithThunk } from 'state/state.common';
import { staticFormEnrichmentReceivedThunk } from 'state/static/staticForm.thunks';
import { getWorkspaceType } from 'state/workspaces/tabs/tabs.selector';

export function staticFormRollbackEpic(timeoutInMillis: number = 60000, appScheduler?: SchedulerLike): EpicWithThunk {
  return (action$, state$, { httpClient, frontLogger }) =>
    action$.pipe(
      ofType('FORM_TAB_ROLLBACKED'),
      filter(action => action.workspace === 'static'),
      withLatestFrom(state$),
      filter(([{ tabId }, state]) => getWorkspaceType(state, tabId) === 'static'),
      mergeMap(([{ tabId }, _state]) => {
        const api = tabsApiConnector(httpClient, 'static');

        const tabRemoved$ = tabRemoved(action$, tabId, state$);

        return api.getTabContent<StaticFormResult>(tabId).pipe(
          takeUntil(tabRemoved$),
          mergeMap(
            ({
              values,
              blocksMetadata,
              fieldsMetadata,
              version: newVersion,
              actions,
              quoteQuery,
              errors,
              productView: newProductView,
              mustForceAdvancedMode,
              allowedRequests,
            }) =>
              of(
                staticFormEnrichmentReceivedThunk(
                  tabId,
                  newProductView,
                  blocksMetadata,
                  fieldsMetadata,
                  values,
                  newVersion,
                  actions,
                  quoteQuery,
                  mustForceAdvancedMode,
                  allowedRequests,
                  errors,
                ),
              ),
          ),
          timeout({
            each: timeoutInMillis,
            with: () => throwError(() => new Error('timeout')),
            scheduler: appScheduler,
          }),
          catchError(error => {
            frontLogger.error('Unable to rollback session on static {@FrontError}', error);
            return of(commonFormActions.fetchFormEnrichmentFailed('static', tabId, error));
          }),
        );
      }),
    );
}
